import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
const AfterSalesDashboard: React.FC = () => {
  const afterSalesKPIs = [{
    title: 'Total Tickets',
    value: '6,890',
    change: '+12.3%',
    isPositive: true
  }, {
    title: 'Avg TAT (Hours)',
    value: '76.8',
    change: '-8.5 hrs',
    isPositive: true
  }, {
    title: 'First-Time Fix %',
    value: '78.4%',
    change: '+2.1%',
    isPositive: true
  }, {
    title: 'Customer Satisfaction Score',
    value: '4.6/5.0',
    change: '+0.2',
    isPositive: true
  }];
  const ticketsTrendData = [{
    month: 'Jan',
    tickets: 5420,
    resolved: 5180
  }, {
    month: 'Feb',
    tickets: 5680,
    resolved: 5420
  }, {
    month: 'Mar',
    tickets: 5950,
    resolved: 5710
  }, {
    month: 'Apr',
    tickets: 6180,
    resolved: 5940
  }, {
    month: 'May',
    tickets: 6420,
    resolved: 6150
  }, {
    month: 'Jun',
    tickets: 6890,
    resolved: 6620
  }];
  const tatComparisonData = [{
    category: 'Metro Cities',
    avgTAT: 68.5,
    target: 72
  }, {
    category: 'Non-Metro',
    avgTAT: 85.2,
    target: 96
  }];
  const issueCategoryData = [{
    category: 'Electrical Issues',
    count: 1850,
    percentage: 26.8,
    color: '#3b82f6'
  }, {
    category: 'Mechanical Failure',
    count: 1520,
    percentage: 22.1,
    color: '#10b981'
  }, {
    category: 'Software/ECU',
    count: 1240,
    percentage: 18.0,
    color: '#f59e0b'
  }, {
    category: 'Body & Trim',
    count: 1080,
    percentage: 15.7,
    color: '#8b5cf6'
  }, {
    category: 'HVAC Systems',
    count: 720,
    percentage: 10.4,
    color: '#ec4899'
  }, {
    category: 'Others',
    count: 480,
    percentage: 7.0,
    color: '#6b7280'
  }];
  const serviceLocationsData = [{
    location: 'Delhi NCR',
    tickets: 1285,
    avgTAT: 65.2,
    firstTimeFix: 82.5,
    satisfaction: 4.7,
    type: 'Metro'
  }, {
    location: 'Mumbai',
    tickets: 1142,
    avgTAT: 68.8,
    firstTimeFix: 80.2,
    satisfaction: 4.6,
    type: 'Metro'
  }, {
    location: 'Bangalore',
    tickets: 1058,
    avgTAT: 70.5,
    firstTimeFix: 79.8,
    satisfaction: 4.6,
    type: 'Metro'
  }, {
    location: 'Chennai',
    tickets: 895,
    avgTAT: 72.3,
    firstTimeFix: 78.4,
    satisfaction: 4.5,
    type: 'Metro'
  }, {
    location: 'Pune',
    tickets: 785,
    avgTAT: 75.8,
    firstTimeFix: 77.2,
    satisfaction: 4.5,
    type: 'Non-Metro'
  }, {
    location: 'Hyderabad',
    tickets: 720,
    avgTAT: 74.2,
    firstTimeFix: 76.8,
    satisfaction: 4.4,
    type: 'Metro'
  }, {
    location: 'Ahmedabad',
    tickets: 685,
    avgTAT: 78.5,
    firstTimeFix: 75.5,
    satisfaction: 4.4,
    type: 'Non-Metro'
  }, {
    location: 'Kolkata',
    tickets: 642,
    avgTAT: 82.3,
    firstTimeFix: 74.2,
    satisfaction: 4.3,
    type: 'Metro'
  }, {
    location: 'Jaipur',
    tickets: 485,
    avgTAT: 88.5,
    firstTimeFix: 72.8,
    satisfaction: 4.2,
    type: 'Non-Metro'
  }, {
    location: 'Lucknow',
    tickets: 428,
    avgTAT: 92.8,
    firstTimeFix: 71.5,
    satisfaction: 4.2,
    type: 'Non-Metro'
  }, {
    location: 'Chandigarh',
    tickets: 395,
    avgTAT: 85.2,
    firstTimeFix: 73.8,
    satisfaction: 4.3,
    type: 'Non-Metro'
  }, {
    location: 'Indore',
    tickets: 375,
    avgTAT: 90.5,
    firstTimeFix: 70.2,
    satisfaction: 4.1,
    type: 'Non-Metro'
  }];
  const getTATColor = (tat: number, type: string) => {
    const threshold = type === 'Metro' ? 72 : 96;
    if (tat <= threshold * 0.9) return 'text-green-600 font-bold';
    if (tat <= threshold) return 'text-yellow-600 font-semibold';
    return 'text-red-600 font-bold';
  };
  const getFixRateColor = (rate: number) => {
    if (rate >= 80) return 'text-green-600 font-bold';
    if (rate >= 75) return 'text-yellow-600 font-semibold';
    return 'text-orange-600 font-semibold';
  };
  const getSatisfactionColor = (score: number) => {
    if (score >= 4.5) return 'text-green-600 font-bold';
    if (score >= 4.2) return 'text-yellow-600 font-semibold';
    return 'text-orange-600 font-semibold';
  };
  return <div data-id="element-18">
      <DescriptionPanel title="After-Sales Service Overview" description="Comprehensive monitoring of service operations including ticket volume, turnaround time, resolution efficiency, and customer satisfaction across all service locations. Track performance metrics and identify areas for operational improvement." insights={['Service TAT improved to 76.8 hours - 8.5 hours better than last quarter', 'Metro cities maintaining 68.5 hour average TAT vs 85.2 hours in non-metro', 'First-time fix rate at 78.4% with consistent improvement trend', 'Electrical issues leading at 26.8% of total tickets - proactive training recommended']} data-id="element-19" />
      {/* After-Sales KPI Row */}
      <div className="mb-8" data-id="element-20">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-21">
          Service Performance Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-22">
          {afterSalesKPIs.map((kpi, index) => <Card key={index} className="p-5 bg-gradient-to-br from-white to-gray-50" data-id="element-23">
              <div className="flex flex-col h-full" data-id="element-24">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide" data-id="element-25">
                  {kpi.title}
                </div>
                <div className="mt-3 text-3xl font-bold text-gray-900" data-id="element-26">
                  {kpi.value}
                </div>
                <div className={`mt-2 text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-27">
                  {kpi.isPositive ? <TrendingUpIcon size={16} className="mr-1" data-id="element-28" /> : <TrendingDownIcon size={16} className="mr-1" data-id="element-29" />}
                  {kpi.change}
                </div>
              </div>
            </Card>)}
        </div>
      </div>
      {/* Tickets Trend */}
      <Card className="p-6 mb-8" data-id="element-30">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-31">
          Monthly Tickets Trend
        </h3>
        <ResponsiveContainer width="100%" height={350} data-id="element-32">
          <LineChart data={ticketsTrendData} data-id="element-33">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-34" />
            <XAxis dataKey="month" tick={{
            fontSize: 12
          }} data-id="element-35" />
            <YAxis tick={{
            fontSize: 12
          }} data-id="element-36" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} data-id="element-37" />
            <Legend data-id="element-38" />
            <Line type="monotone" dataKey="tickets" stroke="#3b82f6" strokeWidth={3} name="Total Tickets" dot={{
            r: 5
          }} data-id="element-39" />
            <Line type="monotone" dataKey="resolved" stroke="#10b981" strokeWidth={3} name="Resolved Tickets" dot={{
            r: 5
          }} data-id="element-40" />
          </LineChart>
        </ResponsiveContainer>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-41">
          <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-42">
            <div className="text-center" data-id="element-43">
              <div className="text-gray-600" data-id="element-44">Current Month</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-45">6,890</div>
            </div>
            <div className="text-center" data-id="element-46">
              <div className="text-gray-600" data-id="element-47">Resolved</div>
              <div className="text-xl font-bold text-green-600" data-id="element-48">6,620</div>
            </div>
            <div className="text-center" data-id="element-49">
              <div className="text-gray-600" data-id="element-50">Resolution Rate</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-51">96.1%</div>
            </div>
          </div>
        </div>
      </Card>
      {/* TAT Comparison and Issue Category */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-52">
        <Card className="p-6" data-id="element-53">
          <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-54">
            TAT: Metro vs Non-Metro Comparison
          </h3>
          <ResponsiveContainer width="100%" height={300} data-id="element-55">
            <BarChart data={tatComparisonData} data-id="element-56">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-57" />
              <XAxis dataKey="category" tick={{
              fontSize: 12
            }} data-id="element-58" />
              <YAxis tick={{
              fontSize: 12
            }} label={{
              value: 'Hours',
              angle: -90,
              position: 'insideLeft'
            }} data-id="element-59" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: number) => `${value} hrs`} data-id="element-60" />
              <Legend data-id="element-61" />
              <Bar dataKey="avgTAT" fill="#3b82f6" name="Avg TAT (Hours)" data-id="element-62" />
              <Bar dataKey="target" fill="#e5e7eb" name="Target TAT" data-id="element-63" />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-64">
            <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-65">
              <div className="text-center" data-id="element-66">
                <div className="text-gray-600" data-id="element-67">Metro Performance</div>
                <div className="text-lg font-bold text-green-600" data-id="element-68">95.1%</div>
                <div className="text-xs text-gray-500" data-id="element-69">vs target</div>
              </div>
              <div className="text-center" data-id="element-70">
                <div className="text-gray-600" data-id="element-71">Non-Metro Performance</div>
                <div className="text-lg font-bold text-green-600" data-id="element-72">88.8%</div>
                <div className="text-xs text-gray-500" data-id="element-73">vs target</div>
              </div>
            </div>
          </div>
        </Card>
        <Card className="p-6" data-id="element-74">
          <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-75">
            Issue Category Breakdown
          </h3>
          <ResponsiveContainer width="100%" height={300} data-id="element-76">
            <PieChart data-id="element-77">
              <Pie data={issueCategoryData} cx="50%" cy="50%" labelLine={false} label={({
              category,
              percentage
            }) => `${category}: ${percentage}%`} outerRadius={100} fill="#8884d8" dataKey="percentage" data-id="element-78">
                {issueCategoryData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-79" />)}
              </Pie>
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: number, name: string, props: any) => [`${props.payload.count} tickets (${value}%)`, props.payload.category]} data-id="element-80" />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>
      {/* Service Locations Table */}
      <Card className="p-6" data-id="element-81">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-82">
          Top Service Locations & Performance Metrics
        </h3>
        <div className="overflow-x-auto" data-id="element-83">
          <table className="w-full" data-id="element-84">
            <thead data-id="element-85">
              <tr className="border-b-2 border-gray-300" data-id="element-86">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-87">
                  Location
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-88">
                  Type
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-89">
                  Total Tickets
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-90">
                  Avg TAT (Hours)
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-91">
                  First-Time Fix %
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-92">
                  Satisfaction
                </th>
              </tr>
            </thead>
            <tbody data-id="element-93">
              {serviceLocationsData.map((location, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-94">
                  <td className="py-3 px-4 text-sm font-semibold text-gray-800" data-id="element-95">
                    {location.location}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-96">
                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${location.type === 'Metro' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-97">
                      {location.type}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-medium text-gray-800" data-id="element-98">
                    {location.tickets.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-sm text-right" data-id="element-99">
                    <span className={getTATColor(location.avgTAT, location.type)} data-id="element-100">
                      {location.avgTAT}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-right" data-id="element-101">
                    <span className={getFixRateColor(location.firstTimeFix)} data-id="element-102">
                      {location.firstTimeFix}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-103">
                    <span className={getSatisfactionColor(location.satisfaction)} data-id="element-104">
                      ★ {location.satisfaction}
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-105">
          <div className="grid grid-cols-5 gap-4 text-sm" data-id="element-106">
            <div className="text-center" data-id="element-107">
              <div className="text-gray-600" data-id="element-108">Total Locations</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-109">12</div>
            </div>
            <div className="text-center" data-id="element-110">
              <div className="text-gray-600" data-id="element-111">Metro Cities</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-112">6</div>
            </div>
            <div className="text-center" data-id="element-113">
              <div className="text-gray-600" data-id="element-114">Non-Metro</div>
              <div className="text-xl font-bold text-gray-600" data-id="element-115">6</div>
            </div>
            <div className="text-center" data-id="element-116">
              <div className="text-gray-600" data-id="element-117">Avg Fix Rate</div>
              <div className="text-xl font-bold text-green-600" data-id="element-118">76.1%</div>
            </div>
            <div className="text-center" data-id="element-119">
              <div className="text-gray-600" data-id="element-120">Avg Satisfaction</div>
              <div className="text-xl font-bold text-green-600" data-id="element-121">4.4/5.0</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default AfterSalesDashboard;